import { Json } from "@redotech/json/json";
import {
  countries,
  territoryCodeToCountryMap,
} from "@redotech/locale/countries";
import { canadaProvinces, usaProvinces } from "@redotech/locale/provinces";
import { shouldChargeMerchantForDamagedOrWrongProduct } from "@redotech/redo-billing-common/damaged-products";
import {
  InjectedLogger,
  noopInjectedLogger,
} from "@redotech/util/injected-logger";
import { dateToInstant } from "@redotech/util/temporal";
import { Big } from "big.js";
import { ObjectId } from "bson";
import { Provider } from "../order";
import { MultipleChoiceTextAnswer } from "../return";
import { getTreatment, Payer, ReturnFlow, Step } from "../return-flow";
import {
  Condition,
  Parameters as ConditionParams,
  Coverage,
  isDamaged,
  isWrongProduct,
} from "../return-flow/condition";
import { ReturnOptionMethod } from "../return-flow/return-option";
import { compareMaybeGids } from "../shopify/gid-utils";
import {
  CompensationMethod,
  CompensationMethodType,
  CompensationType,
  DraftReturn,
  Order as DraftReturnOrder,
  DraftReturn as DraftReturnZod,
  PriceRange,
  ShipmentMethodName,
} from "./draft-return";
import {
  PendingReturnItem,
  ReturnableItem,
  ReturnProduct,
  VariantExchangeItem,
} from "./draft-return-items";

export interface CombinedReturnItem {
  returnableItem: ReturnableItem;
  pendingReturnItem: PendingReturnItem;
}

export const returnOptionMethodMap: {
  [key in CompensationType]: ReturnOptionMethod["type"];
} = {
  exchange: ReturnOptionMethod.EXCHANGE,
  storeCredit: ReturnOptionMethod.STORE_CREDIT,
  refund: ReturnOptionMethod.REFUND,
  repair: ReturnOptionMethod.REPAIR,
};

export const compensationMethodTypeMap: {
  [key in CompensationMethodType]: ReturnOptionMethod["type"];
} = {
  [CompensationMethodType.EXCHANGE]: ReturnOptionMethod.EXCHANGE,
  [CompensationMethodType.STORE_CREDIT]: ReturnOptionMethod.STORE_CREDIT,
  [CompensationMethodType.REFUND]: ReturnOptionMethod.REFUND,
  [CompensationMethodType.REPAIR]: ReturnOptionMethod.REPAIR,
};

export function getCombinedReturnItem(
  draftReturn: DraftReturnZod,
  pendingItemId: string,
): CombinedReturnItem {
  const pendingReturnItem = draftReturn.pendingReturnItems.find(
    (pendingItem) => pendingItem.id.toString() === pendingItemId.toString(),
  );
  if (!pendingReturnItem) {
    throw new Error(`Pending item with id ${pendingItemId} not found`);
  }
  const returnableItem = draftReturn.returnableItems.find(
    (item) => item.id === pendingReturnItem?.returnableItemId,
  );
  if (!returnableItem) {
    throw new Error(
      `Returnable item with id ${pendingReturnItem?.returnableItemId} not found`,
    );
  }
  return { returnableItem, pendingReturnItem };
}

export function getCombinedReturnItems(
  draftReturn: DraftReturnZod,
): CombinedReturnItem[] {
  return draftReturn.pendingReturnItems.map((pendingItem) =>
    getCombinedReturnItem(draftReturn, pendingItem.id),
  );
}

export function getDummyCombinedReturnItems(
  returnableItems: ReturnableItem[],
): CombinedReturnItem[] {
  let itemsBeingReturned = returnableItems.filter(
    (returnableItem) => returnableItem.selected,
  );
  if (itemsBeingReturned.length === 0) {
    // If no items have been selected yet, return dummy items for all returnable items
    itemsBeingReturned = returnableItems;
  }
  return itemsBeingReturned.map((returnableItem) => ({
    returnableItem,
    pendingReturnItem: {
      id: new ObjectId().toString(),
      returnableItemId: returnableItem.id,
      flowPath: [],
      quantity: getPrimaryProduct(returnableItem).quantity,
      inputAnswers: [],
      multipleChoiceAnswers: [],
      returnReasons: [],
      reasonCodes: [],
      flagged: false,
      flow: undefined,
      manualReview: false,
      returnOptions: [],
    },
  }));
}

export const getPrimaryProduct = (
  returnItem: ReturnableItem,
): ReturnProduct => {
  if (returnItem.isExpanded) {
    const primaryProduct = returnItem.products.find(
      (product) =>
        product.id === returnItem.primaryProductId &&
        product.isExpandedPrimaryProduct,
    );
    if (primaryProduct) {
      return primaryProduct;
    }
  }

  const primaryProduct = returnItem.products.find(
    (product) => product.id === returnItem.primaryProductId,
  );
  if (!primaryProduct) {
    throw new Error("Primary product not found");
  }
  return primaryProduct;
};

export const getNonPrimaryProducts = (
  returnItem: ReturnableItem,
): ReturnProduct[] => {
  if (
    returnItem.isExpanded &&
    returnItem.products.some((product) => product.isExpandedPrimaryProduct)
  ) {
    return returnItem.products.filter(
      (product) => !product.isExpandedPrimaryProduct,
    );
  } else {
    return returnItem.products.filter(
      (product) => product.id !== returnItem.primaryProductId,
    );
  }
};

export function getMultipleChoiceAnswers(
  multipleChoiceAnswers: readonly { step: number; answer: number }[],
  flow: ReturnFlow,
  flowPath: readonly number[],
): MultipleChoiceTextAnswer[] {
  const answers: MultipleChoiceTextAnswer[] = [];
  for (const stepIndex of flowPath) {
    const step = flow.steps[stepIndex];

    if (
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      step.type.toString() === Step.MULTIPLE_CHOICE.description ||
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      step.type.description === Step.MULTIPLE_CHOICE.description
    ) {
      const choice = multipleChoiceAnswers.find(
        (answer) => answer.step === stepIndex,
      );

      if (choice) {
        const option = (step as Step.MultipleChoice).options[choice.answer];
        if (option) {
          answers.push({
            answer: option.name,
            questionText: (step as Step.MultipleChoice).message,
            step: stepIndex,
          });
        }
      }
    }
  }
  return answers;
}

export function getCompensationMethod(
  draftReturn: DraftReturnZod,
): CompensationMethod | undefined {
  return draftReturn.compensationMethods?.length === 1
    ? draftReturn.compensationMethods[0]
    : draftReturn.compensationMethods.find(
        (method) => method.type === draftReturn.selectedMethod,
      );
}

export function getPriceAdjustmentForPendingItem(
  compensationMethod: CompensationMethod,
  pendingItem: PendingReturnItem,
) {
  return compensationMethod.adjustments[pendingItem.id.toString()]
    ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      compensationMethod.adjustments[pendingItem.id.toString()]
        .div(pendingItem?.quantity || 1)
        .toFixed(2)
    : null;
}

export function compensationMethodHasRepair(
  compensationMethod: CompensationMethod,
) {
  return Object.values(compensationMethod.itemValues).some(
    (item) => item.strategy === "repair",
  );
}

export function hasReturnItemsThatRequireShipping(
  draftReturn: DraftReturnZod,
): boolean {
  return Object.values(
    getCompensationMethod(draftReturn)?.shippingRequired ?? {},
  ).some(Boolean);
}

export function getShipmentMethod(
  draftReturn: DraftReturnZod,
): ShipmentMethodName {
  return draftReturn?.shipmentMethod?.method ?? ShipmentMethodName.SELF_SHIP;
}

export function getBaseRepairFee(repairFee: PriceRange): number {
  return repairFee[0];
}

export const getReturnableItemTotalReturnValue = (
  returnItem: ReturnableItem | null,
  pendingItemQuantity?: number | null,
): Big => {
  if (!returnItem) {
    return Big(0);
  }
  return (returnItem?.products || []).reduce((acc, product) => {
    if (!returnItem?.isExpanded || product?.isExpandedPrimaryProduct) {
      return acc.plus(product.returnValue).times(pendingItemQuantity ?? 1);
    }
    return acc;
  }, Big(0));
};

export const getReturnableItemTotalTax = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big => {
  return (returnItem?.products || []).reduce((acc, product) => {
    if (!returnItem?.isExpanded || product.isExpandedPrimaryProduct) {
      return acc.plus(product.tax).times(pendingItem?.quantity ?? 1);
    }
    return acc;
  }, Big(0));
};

export const getReturnableItemTotalPrice = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big => {
  return (returnItem?.products || []).reduce((acc, product) => {
    if (!returnItem?.isExpanded || product?.isExpandedPrimaryProduct) {
      return acc
        .plus(product?.originalPrice ?? product.price)
        .times(pendingItem?.quantity ?? 1);
    }
    return acc;
  }, Big(0));
};

export const getReturnableItemTotalGrams = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big => {
  return (returnItem?.products || []).reduce(
    (acc, item) => acc.plus(item.grams).times(pendingItem?.quantity ?? 1),
    Big(0),
  );
};

export const isVariantExchange = (pendingItem: PendingReturnItem) =>
  (!!pendingItem.variantExchangeItem &&
    pendingItem.variantExchangeItem.priceDifference !== "apply") ||
  (pendingItem?.expandedProductVariantExchanges || []).length > 0;

export const isPreBonusUpsell = (pendingItem: PendingReturnItem) =>
  !!pendingItem &&
  !isVariantExchange(pendingItem) &&
  !!pendingItem.advancedExchangeItems?.length;

export function getTotalReturnableValue(items: CombinedReturnItem[]): Big {
  return items.reduce(
    (acc, { pendingReturnItem, returnableItem }) =>
      acc
        .plus(
          getReturnableItemTotalReturnValue(
            returnableItem,
            pendingReturnItem.quantity,
          ),
        )
        .plus(getReturnableItemTotalTax(returnableItem, pendingReturnItem)),
    Big(0),
  );
}

export function isPrimaryProductCoveredByFinalSale(
  primaryItem: ReturnProduct,
  order: DraftReturnOrder | undefined,
): boolean {
  if (!order) {
    return false;
  }

  return (
    order.finalSaleReturnsProtected &&
    order.productsCoveredByFinalSale.includes(primaryItem.productId)
  );
}

export function getTestAndTreatment(draftReturn: DraftReturnZod): {
  testId: string | undefined;
  treatmentId: string | undefined;
} {
  for (const pendingItem of draftReturn.pendingReturnItems) {
    if (pendingItem?.flow) {
      const flow = pendingItem.flow as ReturnFlow;
      for (const pathIndex of pendingItem.flowPath) {
        if (
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          (flow.steps[pathIndex].type as unknown as string) ===
          Step.AB_TEST.description
        ) {
          const testStep = flow.steps[pathIndex] as Step.ABTest;
          const treatment = getTreatment(
            testStep?.treatments || [],
            draftReturn.treatmentHash,
          );
          if (testStep && treatment) {
            return {
              testId: standardizeName(testStep.name),
              treatmentId: standardizeName(treatment?.name),
            };
          }
        }
      }
    }
  }
  return { testId: undefined, treatmentId: undefined };
}

export function standardizeName(name: string) {
  return name.trim().toLowerCase().replace(/\s+/g, "_");
}

export function getReturnOrders(
  draftReturn: DraftReturnZod,
): Record<string, DraftReturnOrder> {
  return draftReturn.pendingReturnItems.reduce<
    Record<string, DraftReturnOrder>
  >((acc: Record<string, DraftReturnOrder>, pendingItem) => {
    const combinedItem = getCombinedReturnItem(draftReturn, pendingItem.id);
    const order = draftReturn.orders.find(
      (order) => order.id === combinedItem.returnableItem.orderId,
    );
    if (order && !acc[order.id]) {
      acc[order.id] = order;
    }
    return acc;
  }, {});
}

/**
 * Get the country code for a given country name.
 *
 * @param name - The name of the country.
 * @param resetTerritoryCodeToCountryCode - Whether to reset the territory code to the country code. see: https://shopify.dev/docs/api/admin-graphql/latest/enums/CountryCode
 * @returns The country code.
 */
export function getCountryCode(
  name: string | null | undefined,
  resetTerritoryCodeToCountryCode = false,
): string | undefined {
  if (!name) {
    return undefined;
  }

  const code = countries.find(
    (country) =>
      country.name.toLowerCase() === name.toLowerCase() ||
      country.code.toLowerCase() === name.toLowerCase(),
  )?.code;

  if (
    code &&
    resetTerritoryCodeToCountryCode &&
    territoryCodeToCountryMap.has(code)
  ) {
    return territoryCodeToCountryMap.get(code) ?? code;
  }

  return code;
}

export function getProvinceCode(
  name: string | null | undefined,
  countryCode: string,
): string | undefined {
  if (!name) {
    return undefined;
  }

  const provinces =
    countryCode === "CA"
      ? canadaProvinces
      : countryCode === "US"
        ? usaProvinces
        : [];

  const nameLower = name.toLowerCase();
  return provinces.find(
    (province) =>
      province.name.toLowerCase() === nameLower ||
      province.code.toLowerCase() === nameLower,
  )?.code;
}

export function getCountryName(
  code: string | null | undefined,
): string | undefined {
  if (!code) {
    return undefined;
  }

  return countries.find(
    (country) =>
      country.name.toLowerCase() === code.toLowerCase() ||
      country.code.toLowerCase() === code.toLowerCase(),
  )?.name;
}

export function buildConditionParams({
  draftReturn,
  pendingReturnItem,
  returnableItem,
  primaryProduct,
  originLocationName,
  order,
  returnMethod,
  compensationMethods,
  numPendingReturnItems,
  totalAdjustmentValue,
  totalPriceOverride,
  timezone,
}: {
  draftReturn: DraftReturnZod;
  pendingReturnItem?: PendingReturnItem;
  returnableItem: ReturnableItem;
  primaryProduct: ReturnProduct;
  originLocationName?: string;
  order: DraftReturnOrder | undefined;
  returnMethod?: ReturnOptionMethod["type"];
  compensationMethods?: ReturnOptionMethod["type"][];
  numPendingReturnItems?: number;
  compensationMethod?: CompensationMethod;
  totalAdjustmentValue?: number;
  totalPriceOverride?: number;
  /** IANA timezone for date-based condition evaluation (e.g., "America/New_York") */
  timezone?: string;
}): ConditionParams {
  //For finalize flow conditions, you will want all returnable items that match a pending item, use this combined items list.
  const pendingItems = draftReturn.returnableItems.filter((item) =>
    draftReturn.pendingReturnItems.some((p) => p.returnableItemId === item.id),
  );
  const customerCountry =
    getCountryCode(draftReturn.customer.shippingAddress?.country) ?? "";
  return {
    coverage: order?.protected ? Coverage.COVERED : Coverage.NOT_COVERED,
    packageProtection: order?.packageProtected
      ? Coverage.COVERED
      : Coverage.NOT_COVERED,
    finalSaleReturns: isPrimaryProductCoveredByFinalSale(primaryProduct, order)
      ? Coverage.COVERED
      : Coverage.NOT_COVERED,
    orderDiscounts: order?.discounts || [], //TODO: Use real discounts once removed
    productDiscounts: returnableItem.products.flatMap(
      (product) => product.productDiscounts || [],
    ), //TODO: Use real discounts
    discountedAmount: returnableItem.products.reduce(
      (acc, product) => acc + (product.productDiscountedAmount ?? 0),
      0,
    ),
    now: Temporal.Now.instant(),
    fulfilledOn: primaryProduct.fulfilledOn.toTemporalInstant(),
    deliveredOn: primaryProduct.deliveredOn.toTemporalInstant(),
    orderedOn: order?.orderDate?.toTemporalInstant() ?? Temporal.Now.instant(),
    lastFulfillmentUpdateOn:
      primaryProduct.lastFulfillmentUpdateOn.toTemporalInstant(),
    customerTags: draftReturn.customer.tags,
    customerEmail:
      draftReturn.customer.shippingAddress?.email ||
      draftReturn.customer.updatedEmail ||
      "",
    orderName: order?.name ?? "",
    customerCountry,
    customerProvince: getProvinceCode(
      draftReturn.customer.shippingAddress?.state,
      customerCountry,
    ),
    price:
      totalPriceOverride ??
      returnableItem.products.reduce(
        (acc, product) => acc + Number(product.price),
        0,
      ),
    totalReturnValue: pendingItems.reduce(
      (acc, item) =>
        acc +
        Number(
          item.products.reduce(
            (acc, product) => acc + Number(product.returnValue),
            0,
          ),
        ),
      0,
    ),
    totalPreDiscountPrice: pendingItems.reduce(
      (acc, item) =>
        acc +
        Number(
          item.products.reduce(
            (acc, product) => acc + Number(product.price),
            0,
          ),
        ),
      0,
    ),
    // This is not the returnable item level but the entire draftReturn level, meaning each returnable item will have the same "nonReturnedValue".
    nonReturnedValue: draftReturn.returnableItems
      .filter(
        (item) =>
          !draftReturn.pendingReturnItems.some(
            (p) => p.returnableItemId === item.id,
          ),
      )
      .reduce(
        (acc, item) =>
          acc +
          Number(
            item.products.reduce(
              (acc, product) => acc + Number(product.returnValue),
              0,
            ),
          ),
        0,
      ),
    returnValue: returnableItem.products.reduce(
      (acc, product) => acc + Number(product.returnValue),
      0,
    ),
    loyaltyAmount: primaryProduct?.loyaltyAmount
      ? +primaryProduct.loyaltyAmount
      : 0,
    sku: [primaryProduct.sku],
    orderTags: order?.tags || [],
    productProperties: Array.from(
      returnableItem.products.reduce((acc, product) => {
        const properties = product.productProperties || {};
        for (const key of Object.keys(properties)) {
          acc.add(key);
        }
        return acc;
      }, new Set<string>()),
    ),
    productTags: [
      ...returnableItem.products.reduce((acc, product) => {
        for (const tag of product.productTags) {
          acc.add(tag);
        }
        return acc;
      }, new Set<string>()),
    ],
    productType: primaryProduct.productType,
    productVariantMetafields: returnableItem.products.reduce(
      (acc, product) => {
        for (const [key, value] of Object.entries(
          product.productVariantMetafields || {},
        )) {
          acc[key] = value;
        }

        return acc;
      },
      {} as Record<string, string>,
    ),
    productMetafields: returnableItem.products.reduce(
      (acc, product) => {
        for (const [key, value] of Object.entries(
          product.productMetafields || {},
        )) {
          acc[key] = value;
        }

        return acc;
      },
      {} as Record<string, string>,
    ),
    collections: returnableItem.products.flatMap(
      (product) => product.collections,
    ),
    orderTimeCollections: returnableItem.products.flatMap(
      (product) => product.orderTimeCollections ?? [],
    ),
    salesChannel: order?.salesChannel ?? "",
    exchangeCount:
      order?.lineItems?.find((lineItem) =>
        compareMaybeGids(lineItem.id, returnableItem.lineItemId),
      )?.exchangeCount ?? 0,
    numCustomerClaims: draftReturn.customer.numCustomerClaims,
    numCustomerReturns: draftReturn.customer.numCustomerReturns,
    numOrders: draftReturn.customer.numOrders,
    // Add orderDates if they exist in the customer object
    orderDates: draftReturn.customer.orderDates?.map(dateToInstant),
    // Add returnDates if they exist in the customer object
    returnDates: draftReturn.customer.returnDates?.map(dateToInstant),
    returnRate: draftReturn.customer.returnRate,
    method: order?.paymentMethods || [],
    numItemsBeingReturned: numPendingReturnItems || 1,
    adjustmentAmount: totalAdjustmentValue ?? 0,
    returnMethod,
    compensationMethods,
    provider: order?.platform || Provider.SHOPIFY,
    purchasedExtendedWarranty:
      !!primaryProduct.extendedWarranty &&
      (primaryProduct.extendedWarranty.quantity ?? 0) -
        (primaryProduct.extendedWarranty.voidedQuantity ?? 0) >
        0,
    warrantyExtension: Temporal.Duration.from({
      days: primaryProduct.extendedWarranty?.daysExtended ?? 0,
    }),
    warrantyActivationDelay: Temporal.Duration.from({
      days: primaryProduct.extendedWarranty?.warrantyActivationDelay ?? 0,
    }),
    returnReasons: pendingReturnItem?.returnReasons || [],
    aiDamaged: pendingReturnItem?.aiDamaged || false,
    aiWrongProduct: pendingReturnItem?.aiWrongProduct || false,
    isAiReason: !!pendingReturnItem?.aiReturnReasonsThreadId,
    channel: draftReturn.channel,
    merchantCreated: draftReturn.merchantCreated ?? undefined,
    // Location flow specific fields
    vendor: primaryProduct.vendor ?? undefined,
    isRepair: pendingReturnItem?.returnReasons?.some((reason) =>
      reason.toLowerCase().includes("repair"),
    ),
    locationName: originLocationName,
    multipleChoiceAnswers:
      pendingReturnItem?.flowPath && pendingReturnItem?.flow
        ? getMultipleChoiceAnswers(
            pendingReturnItem.multipleChoiceAnswers,
            pendingReturnItem.flow,
            pendingReturnItem.flowPath,
          )
        : undefined,
    returnType: draftReturn.returnType ?? undefined,
    timezone,
  };
}

export const getExpandedVariantExchangeProduct = (
  returnableItem: ReturnableItem,
  productId: string | null | undefined,
): ReturnProduct | undefined => {
  if (returnableItem.products.length === 1) {
    return returnableItem.products[0];
  }
  return returnableItem.products.find(
    (product) => product.productId === productId,
  );
};

export const getExpandedReturnItemProducts = (
  combinedItem: CombinedReturnItem,
): ReturnProduct[] => {
  const products: (ReturnProduct | undefined)[] = [];
  if (
    combinedItem.pendingReturnItem?.expandedProductVariantExchanges &&
    (combinedItem.pendingReturnItem.expandedProductVariantExchanges || [])
      .length > 0
  ) {
    for (const variantExchange of combinedItem.pendingReturnItem
      .expandedProductVariantExchanges) {
      products.push(
        getExpandedVariantExchangeProduct(
          combinedItem.returnableItem,
          variantExchange.productId,
        ),
      );
    }
  } else {
    products.push(getPrimaryProduct(combinedItem.returnableItem));
  }
  return products.filter((product) => !!product);
};

export const getExpandedReturnItemVariantExchanges = (
  combinedItem: CombinedReturnItem,
): VariantExchangeItem[] => {
  if (
    combinedItem.pendingReturnItem?.expandedProductVariantExchanges &&
    (combinedItem.pendingReturnItem.expandedProductVariantExchanges || [])
      .length > 0
  ) {
    return combinedItem.pendingReturnItem.expandedProductVariantExchanges;
  } else if (combinedItem.pendingReturnItem?.variantExchangeItem) {
    return [combinedItem.pendingReturnItem.variantExchangeItem];
  } else {
    return [];
  }
};

export const getVariantExchangeItemsAndProducts = (
  draftReturn: DraftReturnZod,
): {
  variant: VariantExchangeItem;
  product: ReturnProduct | undefined;
  quantity: number;
}[] => {
  const variantExchangeItems: {
    variant: VariantExchangeItem;
    product: ReturnProduct | undefined;
    quantity: number;
  }[] = [];
  const compensationMethod = getCompensationMethod(draftReturn);
  if (!compensationMethod) {
    return variantExchangeItems;
  }

  for (const [id, item] of Object.entries(compensationMethod.itemValues)) {
    if (!item.isVariantExchange) {
      continue;
    }

    const combinedItem = getCombinedReturnItem(draftReturn, id);
    if (combinedItem.returnableItem?.isExpanded) {
      const expandedVariantExchanges =
        getExpandedReturnItemVariantExchanges(combinedItem);

      for (const variantExchange of expandedVariantExchanges) {
        variantExchangeItems.push({
          variant: variantExchange,
          product: getExpandedVariantExchangeProduct(
            combinedItem.returnableItem,
            variantExchange.productId,
          ),
          quantity: combinedItem.pendingReturnItem?.quantity ?? 1,
        });
      }
    } else if (combinedItem.pendingReturnItem?.variantExchangeItem) {
      variantExchangeItems.push({
        variant: combinedItem.pendingReturnItem?.variantExchangeItem,
        product: getPrimaryProduct(combinedItem.returnableItem),
        quantity: combinedItem.pendingReturnItem?.quantity ?? 1,
      });
    }
  }

  return variantExchangeItems;
};

export interface ReturnProductItem {
  product: ReturnProduct;
  pendingItem: PendingReturnItem;
}

export const getReturnShippingProducts = (
  draftReturn: DraftReturnZod,
): ReturnProductItem[] => {
  const products: ReturnProductItem[] = [];
  const compensationMethod = getCompensationMethod(draftReturn);
  if (!compensationMethod) {
    return products;
  }

  for (const id of Object.keys(compensationMethod.itemValues)) {
    if (!compensationMethod.shippingRequired[id]) {
      continue;
    }

    const combinedItem = getCombinedReturnItem(draftReturn, id);
    if (combinedItem.returnableItem?.isExpanded) {
      const expandedProducts = getExpandedReturnItemProducts(combinedItem);
      products.push(
        ...expandedProducts.map((product) => ({
          product,
          pendingItem: combinedItem.pendingReturnItem,
        })),
      );
    } else {
      for (const product of combinedItem.returnableItem.products) {
        products.push({ product, pendingItem: combinedItem.pendingReturnItem });
      }
    }
  }

  return products.filter((product) => !!product);
};

export const getReturnNonShippingProducts = (
  draftReturn: DraftReturnZod,
): ReturnProductItem[] => {
  const products: ReturnProductItem[] = [];
  const compensationMethod = getCompensationMethod(draftReturn);
  if (!compensationMethod) {
    return products;
  }

  for (const id of Object.keys(compensationMethod.itemValues)) {
    if (compensationMethod.shippingRequired[id]) {
      continue;
    }

    const combinedItem = getCombinedReturnItem(draftReturn, id);
    if (combinedItem.returnableItem?.isExpanded) {
      const expandedProducts = getExpandedReturnItemProducts(combinedItem);
      products.push(
        ...expandedProducts.map((product) => ({
          product,
          pendingItem: combinedItem.pendingReturnItem,
        })),
      );
    } else {
      for (const product of combinedItem.returnableItem.products) {
        products.push({ product, pendingItem: combinedItem.pendingReturnItem });
      }
    }
  }

  return products;
};

export const getExpandedProductVariantExchangeItem = (
  pendingReturnItem: PendingReturnItem,
  productId: string,
): VariantExchangeItem | undefined => {
  if (pendingReturnItem?.variantExchangeItem) {
    return pendingReturnItem.variantExchangeItem;
  }

  return pendingReturnItem.expandedProductVariantExchanges?.find(
    (exchange) => exchange.productId === productId,
  );
};

export const getRelevantOrders = (
  draftReturn: DraftReturnZod,
): DraftReturnOrder[] => {
  return draftReturn.pendingReturnItems
    .map((item) => {
      const combinedItem = getCombinedReturnItem(draftReturn, item.id);
      return draftReturn.orders.find(
        (order) => order.id === combinedItem.returnableItem.orderId,
      );
    })
    .filter((order) => !!order);
};

export const getRelevantDisplayDiscountCodes = (
  draftReturn: DraftReturnZod,
): string[] => {
  return Array.from(
    new Set(
      getRelevantOrders(draftReturn).flatMap((order) =>
        order.discounts.map((d) => (d.code || d.title).replace(/-RETURN/g, "")),
      ),
    ),
  ).filter((code) => code && code !== "Exchange Credit");
};

export const walkFlow = ({
  draftReturn,
  flow,
  order,
  product,
  returnableItem,
  start,
  logger = noopInjectedLogger,
  timezone,
}: {
  draftReturn: DraftReturnZod;
  flow: ReturnFlow;
  order: DraftReturnOrder | undefined;
  product: ReturnProduct;
  returnableItem: ReturnableItem;
  start: number;
  logger?: InjectedLogger;
  /** IANA timezone for date-based condition evaluation (e.g., "America/New_York") */
  timezone?: string;
}): {
  path: readonly number[];
  step: Step;
  rejected: RejectionDetails | undefined;
} => {
  const path = [];
  let i = start;
  while (i < flow.steps.length) {
    path.push(i);
    const step = flow.steps[i];
    switch (step?.type) {
      case Step.MANUAL_REVIEW:
      case Step.FLAG:
      case Step.ADD_RETURN_TAG:
      case Step.ADD_EXCHANGE_ORDER_TAGS:
        // Move on to the next step
        i = step.next!;
        break;
      case Step.CONDITION: {
        const value = Condition.evaluate(
          step.condition,
          buildConditionParams({
            draftReturn,
            pendingReturnItem: undefined, // No pending item during flow evaluation
            returnableItem,
            primaryProduct: product,
            order,
            returnMethod: undefined,
            compensationMethods: undefined,
            numPendingReturnItems: draftReturn.pendingReturnItems.length,
            totalAdjustmentValue: undefined,
            totalPriceOverride: undefined,
            timezone: timezone ?? draftReturn.customer?.timezone ?? undefined,
          }),
          logger,
        );
        i = value ? step.nextTrue! : step.nextFalse!;
        break;
      }
      case Step.AB_TEST: {
        const treatment = getTreatment(
          step.treatments,
          draftReturn.treatmentHash,
        );
        if (!treatment) {
          throw new Error("No treatment found");
        }
        i = treatment.next;
        break;
      }
      case Step.REJECT:
        return {
          path,
          step,
          rejected: {
            returnableItemId: returnableItem.id.toString(),
            rejectMessage: step.message,
            rejectReason: "",
          },
        };
      case Step.AI_RETURN_REASONS:
      case Step.RETURN:
      case Step.SUBMIT_CLAIM:
      case Step.SUBMIT_REPAIR:
      case Step.SUBMIT_WARRANTY:
      case Step.SUBMIT_WARRANTY_REGISTRATION:
      case Step.EW_UPSELL:
      case Step.BLOCK:
      case Step.INPUT:
      case Step.INFORMATION:
      case Step.MULTIPLE_CHOICE:
        // End of the road, at least for now.
        return { path, step, rejected: undefined };
      default:
        throw new Error(
          `Invalid return flow step type: ${step?.type.description}`,
        );
    }
  }
  return { path, step: flow.steps[i]!, rejected: undefined };
};

export interface RejectionDetails {
  rejectReason: string;
  rejectMessage: string;
  returnableItemId: string;
}

export const getReturnableItemFlow = (
  returnableItem: ReturnableItem | undefined,
  draftReturn: DraftReturnZod | undefined,
): Json => {
  const product = returnableItem?.products.find(
    (p) => p.id === returnableItem?.primaryProductId,
  );
  return product?.reset?.returnFlow &&
    product.reset.returnFlow.type === draftReturn?.flow?.type
    ? {
        steps: JSON.parse(JSON.stringify(product.reset.returnFlow.steps)),
        type: product.reset.returnFlow.type,
      }
    : draftReturn?.flow;
};

export const shouldRemoveMerchantCreatedUpCharge = ({
  deductLabelFromCredit,
  dontChargeMerchantForDamagedOrWrongProductReturnLabels,
  removeMerchantPaidLabelUpcharge,
  draftReturn,
  isFlatRate,
  compensationMethod,
}: {
  deductLabelFromCredit?: boolean;
  dontChargeMerchantForDamagedOrWrongProductReturnLabels?: boolean | null;
  removeMerchantPaidLabelUpcharge?: boolean | null;
  draftReturn: DraftReturn;
  isFlatRate: boolean;
  compensationMethod?: CompensationMethod | null;
}): boolean => {
  if (!removeMerchantPaidLabelUpcharge) {
    return false;
  }

  if (compensationMethod?.payer === Payer.MERCHANT) {
    return true;
  }

  const isFlatRateShipping =
    compensationMethod?.payer !== Payer.REDO && isFlatRate;

  if (isFlatRateShipping) {
    return true;
  }

  const wouldDeductFromCredit =
    compensationMethod?.type === CompensationMethodType.STORE_CREDIT &&
    compensationMethod?.payer !== Payer.REDO &&
    deductLabelFromCredit;

  if (wouldDeductFromCredit) {
    return true;
  }

  return shouldChargeMerchantForDamagedOrWrongProduct({
    damaged: draftReturn.pendingReturnItems.some((p) =>
      isDamaged({
        returnReason: p.returnReason,
        aiDamaged: !!p.aiDamaged,
        isAiReason: !!p.aiReturnReasonsThreadId,
      }),
    ),
    wrongProduct: draftReturn.pendingReturnItems.some((p) =>
      isWrongProduct({
        returnReason: p.returnReason,
        aiWrongProduct: !!p.aiWrongProduct,
        isAiReason: !!p.aiReturnReasonsThreadId,
      }),
    ),
    dontChargeMerchantForDamagedOrWrongProductReturnLabels,
  });
};

export function allOrdersArePaidWithCash(draftReturn: DraftReturnZod): boolean {
  return draftReturn.orders.every((order) => {
    if (order.paymentMethods.length === 0) {
      return false;
    }
    return order.paymentMethods.every(
      (method) => method.toLowerCase().trim() === "cash",
    );
  });
}

/**
 * Splits items sharing a lineItemId into separate bundle instances.
 * Groups by variantId, then pairs by position index within each variant group.
 */
export function splitBundleInstances(
  items: ReturnableItem[],
): ReturnableItem[][] {
  const distinctVariants = new Set(
    items.map((i) => getPrimaryProduct(i).variantId),
  ).size;
  const chunkSize = Math.max(distinctVariants, 1);

  if (items.length <= chunkSize) {
    return [items];
  }

  const byVariant = new Map<string, ReturnableItem[]>();
  for (const item of items) {
    const variantId = getPrimaryProduct(item).variantId;
    let group = byVariant.get(variantId);
    if (!group) {
      group = [];
      byVariant.set(variantId, group);
    }
    group.push(item);
  }

  const instanceCount = Math.max(
    ...Array.from(byVariant.values()).map((g) => g.length),
  );

  const instances: ReturnableItem[][] = [];
  for (let i = 0; i < instanceCount; i++) {
    const instance: ReturnableItem[] = [];
    for (const group of byVariant.values()) {
      if (i < group.length) {
        instance.push(group[i]!);
      }
    }
    instances.push(instance);
  }

  return instances;
}
